import { useMemo } from 'react';
import { startCase } from 'lodash';

import styled from 'styled-components';
import { hourFormat, shortDateFormat } from '@paradigm/utils/src/date';

import { getProductName } from '@paradigm/features/src/products/domain';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import { Trade } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { GrfqTrade } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import {
  primaryProductCode,
  strategyProductCodes,
} from '@paradigm/features/src/unified-rfqs/utils/legs';
import { getProductCodesDescription } from '@paradigm/features/src/unified-rfqs/utils/productCodes';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import { Maybe, Nullable } from '@paradigm/utils/types';
import { QuickActions } from '#/components/UnifiedRfqs/components/QuickActions';
import ProductLabel from '#/components/UnifiedRfqs/components/ProductLabel';
import { lima, razzmatazz } from '@paradigm/design-system/src/colors';
import { LongText } from '#/components/UnifiedRfqs/components/LongText';
import { StrategyDescription } from '#/components/UnifiedRfqs/components/strategy-description/StrategyDescription';
import { AnonIconText } from '#/components/UnifiedRfqs/components/WarningMessage';
import { SpreadFromMarkCellLabel } from '../components/bottom-tabs/SpreadFromMarkCellLabel';
import { SpreadFromMarkColumnLabel } from '../components/bottom-tabs/SpreadFromMarkColumnLabel';

interface ColumnsArgs {
  trade?: Trade;
  counterparty?: Nullable<string>;
  account?: Maybe<string>;
}

export function useColumns({ trade, counterparty, account }: ColumnsArgs = {}) {
  const columns = useMemo(
    () => [
      {
        id: 'date',
        name: 'Date',
        children: !trade ? null : getDate(trade),
      },
      {
        id: 'time',
        name: 'Time',
        children: !trade ? null : getTime(trade),
        mobileOptions: { isMainRow: true },
      },
      {
        id: 'auctionType',
        name: 'Auction',
        mobileOptions: { isHidden: true },
        children: !trade ? null : getAuctionType(trade),
      },
      {
        id: 'product',
        name: 'Product',
        children: !trade ? null : (
          <ProductLabel productCodes={getStrategyProductCodes(trade)}>
            {getProduct(trade)}
          </ProductLabel>
        ),
      },
      {
        id: 'description',
        name: 'Description',
        children: !trade ? null : (
          <StrategyDescription>{getDescription(trade)}</StrategyDescription>
        ),
        mobileOptions: { isMainRow: true },
      },
      {
        id: 'actions',
        name: 'Actions',
        mobileOptions: { hideLabel: true },
        children: !trade ? null : (
          <QuickActions
            baseDDTag="Trade History Quick Actions"
            prefillAccount={account}
            productCode={getTradeProductCode(trade)}
            rfqId={trade.rfq_id}
          />
        ),
      },
      {
        id: 'status',
        name: 'Status',
        mobileOptions: { isHidden: true },
        children: !trade ? null : <Status>{getStatus(trade)}</Status>,
      },
      {
        id: 'counterparty',
        name: 'Counterparty',
        mobileOptions: { isHidden: true },
        children:
          counterparty === undefined ? (
            NEGATIVE_SIGN
          ) : counterparty === null ? (
            <Anon color="white" opacity={0.6} />
          ) : (
            counterparty
          ),
      },
      {
        id: 'quantity',
        name: 'Qty',
        children: !trade ? null : getQuantity(trade),
      },
      {
        id: 'price',
        name: 'Price',
        children: !trade ? (
          NEGATIVE_SIGN
        ) : (
          <span>
            {trade.displayValues.tradePrice}
            <SpreadFromMarkCellLabel value={trade.displayValues.markOffset} />
          </span>
        ),
        mobileOptions: !trade
          ? undefined
          : {
              secondaryLabelContent: <SpreadFromMarkColumnLabel />,
            },
      },
      {
        id: 'refPrice',
        name: 'Ref Price',
        children: trade?.displayValues.indexPrice,
      },
      {
        id: 'side',
        name: 'Side',
        children: !trade ? null : <ActionLabel>{getSide(trade)}</ActionLabel>,
      },
      {
        id: 'quoteCurrency',
        name: 'Quote Currency',
        children: !trade ? null : getQuoteCurrency(trade),
      },

      {
        id: 'role',
        name: 'Role',
        children: !trade ? null : <Role>{getRole(trade)}</Role>,
      },
      {
        id: 'rejectParty',
        name: 'Reject Party',
        children: !trade ? null : getRejectParty(trade),
      },
      {
        id: 'rejectReason',
        name: 'Reject Reason',
        children: !trade ? null : (
          <RejectReason>{getRejectReason(trade)}</RejectReason>
        ),
      },
      {
        id: 'tradeId',
        name: 'Trade ID',
        children: !trade ? null : (
          <LongText tooltip={`${getTradeId(trade)}`}>
            {getTradeId(trade)}
          </LongText>
        ),
      },

      {
        id: 'rfqId',
        name: 'RFQ ID',
        mobileOptions: { isHidden: true },
        children: !trade ? null : (
          <LongText tooltip={`${getRfqId(trade)}`}>{getRfqId(trade)}</LongText>
        ),
      },
      {
        id: 'account',
        name: 'Account',
        children: <Account> {account}</Account>,
      },
    ],
    [trade, account, counterparty],
  );

  return columns;
}

export default useColumns;

const isGrfq = (trade: Trade): trade is GrfqTrade => trade.rfqType === 'grfq';

const getAuctionType = (trade: Trade) => (isGrfq(trade) ? 'OB' : 'RFQ');

// trade.legs may include a hedge leg on a different product, which must
// not count towards "is this multi-product" — see strategyProductCodes.
// A GRFQ is never multi-product, and its product_codes carries the hedge
// too, so only the primary code belongs in the set.
const getStrategyProductCodes = (trade: Trade) =>
  isGrfq(trade)
    ? trade.product_codes.slice(0, 1)
    : strategyProductCodes(trade.legs);

export const getProduct = (trade: Trade) => {
  if (isGrfq(trade)) return getProductName(getTradeProductCode(trade));
  const legProductCodes = getStrategyProductCodes(trade);
  return legProductCodes.length > 1
    ? getProductCodesDescription(legProductCodes)
    : getProductName(getTradeProductCode(trade));
};

const getDescription = (trade: Trade) => trade.description;

const getSide = (trade: Trade) => (isGrfq(trade) ? trade.action : trade.side);

const getRejectReason = (trade: Trade) => trade.rejected_reason;

const getTradeId = (trade: Trade) => trade.id;

const getRfqId = (trade: Trade) => trade.rfq_id;

const getQuoteCurrency = (trade: Trade) => trade.quote_currency;

const getRole = (trade: Trade) =>
  isGrfq(trade) ? trade.desk_role ?? NEGATIVE_SIGN : trade.role.toLowerCase();
const getRejectParty = (trade: Trade) =>
  isGrfq(trade) ? NEGATIVE_SIGN : trade.rejected_party ?? NEGATIVE_SIGN;

const getTradeIsDated = (trade: Trade) =>
  isGrfq(trade)
    ? false
    : isDatedRfqLegs(trade.legs, getTradeProductCode(trade));

const getQuantity = (trade: Trade) =>
  formatProductQty(
    trade.quantity,
    getTradeProductCode(trade),
    !isGrfq(trade),
    getTradeIsDated(trade),
  );

const getStatus = (trade: Trade) =>
  isGrfq(trade) ? trade.status : trade.state;

export const getTradeProductCode = (trade: Trade) =>
  isGrfq(trade)
    ? trade.product_codes[0]
    : // A trade has no product_codes to fall back on, so fall back to
      // the first leg: without a code the row loses both its product
      // name and its quantity precision.
      primaryProductCode(trade.legs) ?? trade.legs[0]?.product_code;

const getTime = (trade: Trade) =>
  isGrfq(trade)
    ? hourFormat(trade.traded)
    : hourFormat(
        trade.state === 'FILLED' ? trade.filled_at : trade.executed_at,
      );

const getDate = (trade: Trade) =>
  shortDateFormat(
    isGrfq(trade)
      ? trade.traded
      : trade.state === 'FILLED'
      ? trade.filled_at
      : trade.executed_at,
  );

function Status({ children }: { children: string }) {
  return <span>{startCase(children.toLowerCase())}</span>;
}

const Role = styled.span`
  text-transform: capitalize;
`;

const Account = styled.span`
  grid-area: account;
`;

function RejectReason({ children }: { children?: string | null }) {
  if (children == null) return <span>{NEGATIVE_SIGN}</span>;
  return <LongText tooltip={children}>{children}</LongText>;
}

const ActionLabel = styled.span`
  ${(props) => props.children === 'SELL' && `color: ${razzmatazz};`}
  ${(props) => props.children === 'BUY' && `color: ${lima};`}
`;

const Anon = styled(AnonIconText)`
  opacity: 0.6;
  justify-content: left;
`;
